import { AfterContentInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MediaService } from '../lib/media.service';
import { forkJoin } from 'rxjs';
import { DeviceLayout, EcgConfiguration } from '../app/resolvers/ecg-configuration';
import { EcgViewerComponent } from '../ecg-viewer-module/ecg-viewer/ecg-viewer.component';
import { IEcgData } from '../ecg-viewer-module/models/iecgdata';
import { EcgState } from '../ecg-viewer-module/view-models/ecgstate';
import { CultureMappingService } from '../ecg-viewer-module/utils/culture.service';
import { PostMessage, PostMessageService, PostMessageType } from '../ecg-viewer-module/utils/post-message.service';
import { BaseStudiesLoaderService } from '../ecg-viewer-module/utils/studies-loader.service';
import { EcgMultiViewer } from './ecg-multiviewer.component';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ECGState } from '../store/reducers/ecg-viewer.reducer';
import { selectorFullState } from '../store/selectors/ecg-viegew.selectors';
import { ecgViewerActions } from '../store/actions';
import { HeaderInformationComponent } from '../ecg-viewer-module/header-information/header-information.component';
import { SeCheckedListBoxComponent } from './se-checkedlistbox/se-checkedlistbox.component';
import { SeCheckedItemComponent } from './se-checkedlistbox/se-checkeditem.component';
import { DateTimePipe } from '../ecg-viewer-module/utils/datetime.pipe';
import { NgForOf } from '@angular/common';

@Component({
    selector: 'multi-viewer',
    templateUrl: './multiviewer.component.html',
    styleUrls: ['./multiviewer.component.css'],
    viewProviders: [PostMessageService],
    imports: [
        HeaderInformationComponent,
        SeCheckedListBoxComponent,
        SeCheckedItemComponent,
        EcgViewerComponent,
        TranslatePipe,
        DateTimePipe,
        NgForOf,
    ],
    standalone: true,
})
export class MultiViewerComponent extends EcgMultiViewer implements OnInit, AfterContentInit {
    // data members
    @ViewChild(EcgViewerComponent)
    ecgViewer!: EcgViewerComponent;

    @Input()
    ecgConfiguration!: EcgConfiguration;

    @Input()
    ecgState = new EcgState();

    // constructor
    constructor(
        private translate: TranslateService,
        private cultureMappingService: CultureMappingService,
        private route: ActivatedRoute,
        private studiesLoaderService: BaseStudiesLoaderService,
        private postMessageService: PostMessageService,
        private mediaService: MediaService,
        private actions: Actions,
        private ecgStore: Store<ECGState>,
    ) {
        super();

        this.studiesLoaderService.streamsReady.subscribe(
            (streams) => {
                this.chart.isBusy = true;

                setTimeout(() => {
                    forkJoin(streams).subscribe({
                        next: (ecgDataList) => {
                            try {
                                ecgDataList.forEach((ecgData: IEcgData) => {
                                    this.addLayer(ecgData);
                                });

                                if (this.layers.length > 0) this.activateLayer(this.layers[0]);

                                this.sendMessageToBrowser(new PostMessage(null, PostMessageType.initialized));
                            } catch (exception) {
                                this.sendMessageToBrowser(
                                    new PostMessage(null, PostMessageType.initialized, exception),
                                );
                            }
                        },
                        error: (error) => {
                            this.sendMessageToBrowser(new PostMessage(null, PostMessageType.initialized, error));
                        },
                    });

                    this.chart.isBusy = false;
                }, 100);
            },
            (error) => {
                this.chart.isBusy = false;
                this.sendMessageToBrowser(new PostMessage(null, PostMessageType.initialized, error));
            },
        );

        this.studiesLoaderService.retrieveStreams();
    }

    get isMobileVersion() {
        return (
            !this.mediaService.isDesktop ||
            (this.ecgConfiguration != null && this.ecgConfiguration.deviceLayout.value == DeviceLayout.mobile)
        );
    }

    // public functions
    ngOnInit() {
        this.ecgConfiguration = this.route.snapshot.data['ecgConfiguration'] as EcgConfiguration;
    }

    ngAfterContentInit() {
        this.listenForEvents();
    }

    // private functions
    private sendMessageToBrowser(message: PostMessage) {
        this.postMessageService.send(message);
    }

    private listenForEvents() {
        this.ecgState = this.ecgStore.selectSignal(selectorFullState)();
        this.updateCulture();
        this.ecgViewer?.ecgChart?.updateState();

        this.actions.pipe(ofType(ecgViewerActions.detailsVisibleMessage)).subscribe(() => {
            this.ecgViewer?.ecgChart?.fit();
        });
    }

    private updateCulture() {
        let resourceName = this.cultureMappingService.getResourceNameByCultureType(this.ecgState.culture);
        this.translate.use(resourceName);
    }
}
